import { useCallback, useRef, useState } from 'react';
import { SystemContextLabel } from '@/components/SystemContextLabel';
import { BulkUploadFileIcon } from '@/components/icons/BulkUploadFileIcon';
import { isCsvFile } from '@/utils/csvAttachment';
import { downloadBulkUserImportTemplate } from '@/utils/downloadBulkUserTemplate';

interface BulkImportEmptyUploadProps {
  systemLabel: string;
  onFileSelect: (file: File) => void;
  onError: (message: string) => void;
}

export function BulkImportEmptyUpload({
  systemLabel,
  onFileSelect,
  onError,
}: BulkImportEmptyUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      if (!isCsvFile(file)) {
        onError('Only .csv files are supported.');
        return;
      }
      onFileSelect(file);
    },
    [onError, onFileSelect]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="bulk-import__empty">
      <header className="bulk-import__empty-header">
        <h2 className="bulk-import__empty-title">Bulk add users</h2>
        <SystemContextLabel
          label={systemLabel}
          className="bulk-import__empty-system"
          iconVariant="plain"
          iconSize={18}
        />
      </header>

      <div className="bulk-import__empty-body">
        <div
          className={`bulk-import__empty-dropzone${dragActive ? ' bulk-import__empty-dropzone--active' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="bulk-import__empty-icon-wrap" aria-hidden>
            <BulkUploadFileIcon className="bulk-import__empty-file-icon" />
          </div>
          <div className="bulk-import__empty-copy">
            <p className="bulk-import__empty-prompt">
              Drag &amp; drop files or{' '}
              <button
                type="button"
                className="bulk-import__empty-browse"
                onClick={() => inputRef.current?.click()}
              >
                Browse
              </button>
            </p>
            <p className="bulk-import__empty-format">Supported format: CSV</p>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="bulk-import__empty-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              handleFile(file);
            }}
          />
        </div>

        <section className="bulk-import__empty-guide" aria-labelledby="bulk-import-guide-heading">
          <h3 id="bulk-import-guide-heading" className="bulk-import__empty-guide-title">
            How to add people:
          </h3>
          <ol className="bulk-import__empty-guide-list">
            <li>Download the template</li>
            <li>
              Fill in one row per person with their name, email, and the credentials they need,
              such as Mobile, PIN, or Card.
            </li>
            <li>Upload your filled file above</li>
          </ol>
        </section>

        <button
          type="button"
          className="bulk-import__empty-download-btn"
          onClick={downloadBulkUserImportTemplate}
        >
          Download template
        </button>
      </div>
    </div>
  );
}
